import apiClient from './apiClient';

/**
 * Self-service billing API types matching backend DTOs.
 * @see apps/backend/src/billing/dto/billing-data.dto.ts
 * @see apps/backend/src/billing/dto/self-service-orders.dto.ts
 */

export interface SelfServiceBillingData {
  companyName: string | null;
  nip: string | null;
  street: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  billingEmail: string | null;
  source: 'party' | 'legacy';
}

export interface SaveBillingDataPayload {
  nip: string;
  companyName?: string | null;
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string;
  billingEmail?: string;
}

export interface SelfServiceOrderListItem {
  id: string;
  orderNumber: string;
  status: 'CONFIRMED' | 'FULFILLED' | 'CLOSED' | 'CANCELED';
  totalGrossCents: number;
  description: string;
  createdAt: string;
}

export interface SelfServiceOrderListResponse {
  items: SelfServiceOrderListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface SelfServicePartySnapshot {
  companyName: string;
  nip: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
  };
  billingEmail: string | null;
}

export interface SelfServiceOrderDocument {
  id: string;
  type: string;
  documentNumber: string;
  status: string;
  amountNetCents: number;
  amountGrossCents: number;
  issuedAt: string;
  dueDate: string | null;
  proxyPdfUrl: string;
}

export interface SelfServiceOrderLine {
  id: string;
  description: string;
  billingInterval: string | null;
  periodStart: string;
  periodEnd: string;
  sortOrder: number;
  pricing: {
    type: string;
    netCents: number | null;
    grossCents: number | null;
  } | null;
}

export interface SelfServiceOrderDetail {
  id: string;
  orderNumber: string;
  status: string;
  totalNetCents: number;
  totalGrossCents: number;
  vatRate: number;
  currency: string;
  paymentMethod: string;
  canceledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
  lines: SelfServiceOrderLine[];
  documents: SelfServiceOrderDocument[];
  partySnapshot: SelfServicePartySnapshot | null;
}

export interface GusLookupData {
  companyName: string;
  street: string;
  city: string;
  postalCode: string;
  regon?: string;
}

/**
 * Self-service billing API client.
 *
 * Endpoints:
 * - GET  /billing/self-service/billing-data   (PA-7 AC-1)
 * - POST /billing/self-service/billing-data   (PA-7 AC-3)
 * - GET  /billing/self-service/orders         (PA-7 AC-7)
 * - GET  /billing/self-service/orders/:id     (PA-7 AC-9)
 * - GET  /billing/self-service/gus-lookup     (GUS-F AC-B1)
 */
export const selfServiceBillingApi = {
  /** Fetch current billing data for tenant (AC-1) */
  getBillingData: async (): Promise<SelfServiceBillingData> => {
    const { data } = await apiClient.get<SelfServiceBillingData>(
      '/billing/self-service/billing-data',
    );
    return data;
  },

  /** Save billing data (AC-3) */
  saveBillingData: async (payload: SaveBillingDataPayload): Promise<void> => {
    await apiClient.post('/billing/self-service/billing-data', payload);
  },

  /** Fetch paginated order list (AC-7) */
  getOrders: async (params: {
    limit: number;
    offset: number;
  }): Promise<SelfServiceOrderListResponse> => {
    const { data } = await apiClient.get<SelfServiceOrderListResponse>(
      '/billing/self-service/orders',
      { params },
    );
    return data;
  },

  /** Fetch order detail (AC-9) */
  getOrderDetail: async (orderId: string): Promise<SelfServiceOrderDetail> => {
    const { data } = await apiClient.get<SelfServiceOrderDetail>(
      `/billing/self-service/orders/${orderId}`,
    );
    return data;
  },

  /** Lookup company data from GUS by NIP (AC-B1) */
  gusLookup: async (nip: string): Promise<GusLookupData> => {
    const { data } = await apiClient.get<GusLookupData>(
      '/billing/self-service/gus-lookup',
      { params: { nip } },
    );
    return data;
  },
};
